import { KeyEvent, Keys, Random, Scene, vec } from 'excalibur';
import { DriftingDoodle } from '../actors/drifting-doodle';
import { PaperBackground } from '../actors/paper-background';
import { trackEvent } from '../monetization/events';
import { fetchTopScores, playerName } from '../monetization/leaderboard';
import { refreshSceneTheme } from '../themes/theme';
import { sfx } from '../utils/audio';
import { DESIGN_WIDTH, SEEDS } from '../utils/constants';
import { PlacementRect, randomPointInRects } from '../utils/placement';
import { DOODLE_KINDS } from '../utils/sketch';
import { HandLabel } from '../ui/label';
import { SketchButton } from '../ui/sketch-button';

const ROW_COUNT = 10;
const ROW_TOP = 292;
const ROW_GAP = 74;

const SCORES_DOODLE_ZONES: PlacementRect[] = [
  { left: 16, top: 120, right: 84, bottom: 1240 },
  { left: 636, top: 120, right: 704, bottom: 1240 },
];

export class ScoresScene extends Scene {
  private rankLabels: HandLabel[] = [];
  private nameLabels: HandLabel[] = [];
  private scoreLabels: HandLabel[] = [];
  private status!: HandLabel;

  onInitialize(): void {
    this.add(new PaperBackground());
    this.add(
      new HandLabel({
        name: 'scores-header',
        text: 'leaderboard',
        pos: vec(DESIGN_WIDTH / 2, 92),
        size: 34,
        tone: 'inkSoft',
      }),
    );
    this.add(
      new HandLabel({
        name: 'scores-heading',
        text: 'top scores',
        pos: vec(DESIGN_WIDTH / 2, 160),
        size: 56,
        tone: 'ink',
      }),
    );

    for (let i = 0; i < ROW_COUNT; i++) {
      const y = ROW_TOP + i * ROW_GAP;
      const rank = new HandLabel({
        name: `scores-rank-${i}`,
        text: '',
        pos: vec(112, y),
        size: 32,
        tone: 'inkSoft',
        anchor: vec(1, 0.5),
      });
      const name = new HandLabel({
        name: `scores-name-${i}`,
        text: '',
        pos: vec(142, y),
        size: 32,
        tone: 'ink',
        anchor: vec(0, 0.5),
      });
      const score = new HandLabel({
        name: `scores-score-${i}`,
        text: '',
        pos: vec(608, y),
        size: 32,
        tone: 'blueDeep',
        anchor: vec(1, 0.5),
      });
      this.rankLabels.push(rank);
      this.nameLabels.push(name);
      this.scoreLabels.push(score);
      this.add(rank);
      this.add(name);
      this.add(score);
    }

    this.status = new HandLabel({
      name: 'scores-status',
      text: 'loading...',
      pos: vec(DESIGN_WIDTH / 2, 620),
      size: 30,
      tone: 'inkSoft',
    });
    this.add(this.status);

    const back = new SketchButton({
      name: 'scores-back',
      text: 'back',
      width: 220,
      height: 56,
      accentTone: 'yellow',
      fontSize: 28,
      onPress: this.goBack,
    });
    back.pos = vec(DESIGN_WIDTH / 2, 1150);
    this.add(back);

    const rng = new Random(SEEDS.drifting + 55);
    for (let i = 0; i < 6; i++) {
      this.add(
        new DriftingDoodle({
          name: `scores-doodle-${i}`,
          kind: DOODLE_KINDS[(i + 4) % DOODLE_KINDS.length],
          pos: randomPointInRects(rng, SCORES_DOODLE_ZONES),
          size: rng.floating(12, 18),
          toneIndex: i + 2,
          seed: SEEDS.drifting + 500 + i * 17,
        }),
      );
    }
  }

  onActivate(): void {
    refreshSceneTheme(this);
    this.clearRows();
    this.status.text = 'loading...';
    this.input.keyboard.on('press', this.handleKey);
    trackEvent('scores_opened');
    void this.loadScores();
  }

  onDeactivate(): void {
    this.input.keyboard.off('press', this.handleKey);
  }

  private clearRows(): void {
    for (const label of [...this.rankLabels, ...this.nameLabels, ...this.scoreLabels]) {
      label.text = '';
    }
  }

  private async loadScores(): Promise<void> {
    const rows = await fetchTopScores(ROW_COUNT);
    this.clearRows();
    if (rows.length === 0) {
      this.status.text = 'no scores yet — be the first';
      return;
    }
    this.status.text = '';
    const me = playerName();
    rows.forEach((row, index) => {
      if (index >= ROW_COUNT) {
        return;
      }
      this.rankLabels[index].text = `${row.rank}.`;
      this.nameLabels[index].text = row.name;
      this.nameLabels[index].setTone(row.name === me ? 'blueDeep' : 'ink');
      this.scoreLabels[index].text = String(row.score);
    });
  }

  private goBack = (): void => {
    sfx.resume();
    sfx.start();
    void this.engine.goToScene('title');
  };

  private handleKey = (evt: KeyEvent): void => {
    if (evt.key === Keys.Escape || evt.key === Keys.Backspace) {
      this.goBack();
    }
  };
}
